/* Libraries */
import React, {
  FC,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { snakeCase } from 'lodash';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

/* Hooks */
import usePageScroll from '~/src/hooks/usePageScroll';
import useTemplateDefaultValuesPagination from '~/src/hooks/useTemplateDefaultValuesPagination';

/* Hooks */
import { useTemplatePreview } from '~/src/hooks/useTemplatePreview';

/* Components */
import TemplateDefaultValuesList from '~/src/components/TemplateDefaultValuesList/TemplateDefaultValuesList';
import TemplateDefaultValuesListEmptyView from '~/src/components/TemplateDefaultValuesList/TemplateDefaultValuesListEmptyView';
import LoadMoreDisplayText from '~/src/components/LoadMoreDisplayText';
import TemplatePreviewDialog from '~/src/components/TemplatePreviewDialog';

/* Services */
import templateDefaultValuesApiService from '~/src/services/api/templateDefaultValuesApiService';
import { OutdatedTemplate } from '~/src/services/api/templateSetsService';

/* Store */
import useMst from '~/src/hooks/useMst';
import { getOrgFprintFromStoreNode } from '~/src/stores/utils';

/* Types */
import { TemplateDefaultValues } from '~/src/models';
import { SortByOrder } from '~/src/hooks/sortByOrder';
import { Actions } from '../RowActions/RowActions';

interface PendingAdd {
  templateDefaultValues: TemplateDefaultValues;
  outdated: OutdatedTemplate;
}

interface Props {
  addFormTemplateToSelectionSidebar: (t: TemplateDefaultValues) => void;
  containedInSidebar: (t: TemplateDefaultValues) => boolean;
  rowActions?: Actions<TemplateDefaultValues>;
  sidebarItemTotalNumber: number;
  sidebarItemTotalNumberLimit: number;
}

const TemplateDefaultValuesListWrapper: FC<Props> = ({
  addFormTemplateToSelectionSidebar,
  containedInSidebar,
  rowActions,
  sidebarItemTotalNumber,
  sidebarItemTotalNumberLimit,
}) => {
  const { next, loading, empty, done, data, setSortBy, setOrder, setFilters } =
    useTemplateDefaultValuesPagination();

  const [pendingAdd, setPendingAdd] = useState<PendingAdd | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const {
    previewTitle,
    previewImages,
    isPreviewLoading,
    isPreviewOpen,
    openPreview,
    closePreview,
  } = useTemplatePreview();

  const { refreshFormTemplate } = useMst((store) => ({
    refreshFormTemplate: store.templateDefaultValues.refreshFormTemplate,
  }));

  // Store latest function references to avoid dependency issues
  const setFiltersRef = useRef(setFilters);
  const setSortByRef = useRef(setSortBy);
  const setOrderRef = useRef(setOrder);
  const nextRef = useRef(next);

  // Update refs when hook functions change
  useEffect(() => {
    setFiltersRef.current = setFilters;
  }, [setFilters]);

  useEffect(() => {
    setSortByRef.current = setSortBy;
  }, [setSortBy]);

  useEffect(() => {
    setOrderRef.current = setOrder;
  }, [setOrder]);

  useEffect(() => {
    nextRef.current = next;
  }, [next]);

  const tableData = useMemo(() => {
    return data.map((templateDefaultValues: TemplateDefaultValues) => {
      const tpl =
        (templateDefaultValues.template as any)?.toJSON?.() ??
        templateDefaultValues.template;
      return {
        id: templateDefaultValues.id,
        title: templateDefaultValues.title || 'untitled',
        lastModified: templateDefaultValues.lastModified,
        template: templateDefaultValues.template,
        isSourceDeprecated: templateDefaultValues.isSourceDeprecated,
        courtFormTitle: (tpl?.title ?? tpl?.subtitle ?? '') as string,
      };
    });
  }, [data]);

  const pageScroll = usePageScroll();

  const handleOnPageScroll = useCallback(() => {
    if ((pageScroll === 0 || pageScroll >= 85) && !loading) {
      nextRef.current();
    }
  }, [pageScroll, loading]);

  useEffect(() => {
    if (pageScroll === 0 || pageScroll >= 85 || pageScroll === 100) {
      handleOnPageScroll();
    }
  }, [pageScroll, handleOnPageScroll]);

  // Memoize the empty state calculation
  const calculatedEmpty = useMemo(() => {
    return !loading && done && tableData.length === 0;
  }, [loading, done, tableData.length]);

  // Memoize the visibleItems calculation
  const calculatedVisibleItems = useMemo(() => {
    return tableData.length;
  }, [tableData.length]);

  const isAddButtonDisabled = useMemo(
    () => sidebarItemTotalNumber >= sidebarItemTotalNumberLimit,
    [sidebarItemTotalNumber, sidebarItemTotalNumberLimit],
  );

  const getAddButtonTooltip = useMemo(
    () =>
      sidebarItemTotalNumber >= sidebarItemTotalNumberLimit
        ? 'Cannot add more than 20 documents to selection.'
        : null,
    [sidebarItemTotalNumber, sidebarItemTotalNumberLimit],
  );

  const handleSearchChange = useCallback((searchValue: string) => {
    if (searchValue) {
      setFiltersRef.current({
        search: searchValue,
        sortBy: 'title',
        order: SortByOrder.asc,
      });
    } else {
      setFiltersRef.current({
        search: searchValue,
        sortBy: 'last_modified',
        order: SortByOrder.desc,
      });
    }
  }, []);

  const handleSortByChange = useCallback((property: string, order: number) => {
    if (!property) {
      return;
    }

    const orderLabel = order > 0 ? SortByOrder.desc : SortByOrder.asc;
    let sortBy = snakeCase(property);

    if (property === 'updatedAt') {
      sortBy = 'last_modified';
    }

    setSortByRef.current(sortBy);
    setOrderRef.current(orderLabel);
  }, []);

  const handleAddWithVersionCheck = useCallback(
    async (templateDefaultValues: TemplateDefaultValues) => {
      try {
        const orgFprint = getOrgFprintFromStoreNode();
        const result =
          await templateDefaultValuesApiService.checkFormTemplateVersion(
            orgFprint,
            templateDefaultValues.id,
          );

        const upgradeable = result.outdated_templates;

        const first = upgradeable[0];
        if (upgradeable.length === 0 || !first) {
          addFormTemplateToSelectionSidebar(templateDefaultValues);
        } else {
          setPendingAdd({ templateDefaultValues, outdated: first });
        }
      } catch {
        addFormTemplateToSelectionSidebar(templateDefaultValues);
      }
    },
    [addFormTemplateToSelectionSidebar],
  );

  const handleCancelDialog = () => {
    setPendingAdd(null);
  };

  const handleDismissDialog = () => {
    if (!pendingAdd) return;
    addFormTemplateToSelectionSidebar(pendingAdd.templateDefaultValues);
    setPendingAdd(null);
  };

  const handleConfirmUpgrade = async () => {
    if (!pendingAdd) return;
    setIsUpgrading(true);
    try {
      const orgFprint = getOrgFprintFromStoreNode();
      await templateDefaultValuesApiService.upgradeFormTemplate(
        orgFprint,
        pendingAdd.templateDefaultValues.id,
      );
      // Re-fetch from backend so the store has the new template (docId=1196 not 1200)
      const refreshed = await refreshFormTemplate(
        pendingAdd.templateDefaultValues.id,
      );
      addFormTemplateToSelectionSidebar(
        refreshed ?? pendingAdd.templateDefaultValues,
      );
    } catch {
      addFormTemplateToSelectionSidebar(pendingAdd.templateDefaultValues);
    } finally {
      setIsUpgrading(false);
      setPendingAdd(null);
    }
  };

  const dialogOutdated = pendingAdd?.outdated;

  if (empty) {
    return (
      <>
        <TemplateDefaultValuesListEmptyView />
      </>
    );
  }

  return (
    <>
      <TemplateDefaultValuesList
        templateDefaultValues={tableData}
        visibleItems={calculatedVisibleItems}
        empty={calculatedEmpty}
        rowActions={rowActions}
        containedInSidebar={containedInSidebar}
        isAddButtonDisabled={isAddButtonDisabled}
        getAddButtonTooltip={getAddButtonTooltip}
        addFormTemplateToSelectionSidebar={handleAddWithVersionCheck}
        onSearchChange={handleSearchChange}
        onSortByChange={handleSortByChange}
      />
      <LoadMoreDisplayText
        loading={loading}
        done={done}
        onLoadMore={nextRef.current}
      />

      <Dialog
        open={!!pendingAdd}
        onClose={handleCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Newer Version Available</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {dialogOutdated ? (
              <>
                <div>
                  This form template contains an older version:
                  <br />
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <strong>
                      <em>
                        {dialogOutdated.current_form_number ??
                          `Document ${dialogOutdated.current_pdf_doc_id}`}
                        {dialogOutdated.current_form_name
                          ? `, ${dialogOutdated.current_form_name}`
                          : ''}{' '}
                        (#{dialogOutdated.current_global_template_id})
                      </em>
                    </strong>
                    <Button
                      size="small"
                      onClick={() =>
                        openPreview(
                          dialogOutdated.current_global_template_id,
                          dialogOutdated.current_form_number ??
                            `Document ${dialogOutdated.current_pdf_doc_id}`,
                        )
                      }
                    >
                      Preview
                    </Button>
                  </div>
                </div>
                <br />
                <div>
                  A newer version is available:
                  <br />
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <strong>
                      <em>
                        {dialogOutdated.new_form_number
                          ? `${dialogOutdated.new_form_number}${
                              dialogOutdated.new_form_name
                                ? `, ${dialogOutdated.new_form_name}`
                                : ''
                            }`
                          : `Document ${dialogOutdated.new_pdf_doc_id}`}{' '}
                        (#{dialogOutdated.new_global_template_id})
                      </em>
                    </strong>
                    <Button
                      size="small"
                      onClick={() =>
                        openPreview(
                          dialogOutdated.new_global_template_id,
                          dialogOutdated.new_form_number ??
                            `Document ${dialogOutdated.new_pdf_doc_id}`,
                        )
                      }
                    >
                      Preview
                    </Button>
                  </div>
                </div>
                <br />
                Would you like to upgrade to the newer version before adding to
                your selection?
                <br />
                <br />
                <Alert severity="warning" style={{ textAlign: 'left' }}>
                  Some values from the current version may not carry over after
                  upgrading.
                  <br />
                  All checkbox values (check marks) will not be migrated.
                  <br />
                  Some text field values may also be lost if the corresponding
                  tags do not exist in the newer version.
                  <br />
                  If the form layout changed between versions, some text field
                  values may be assigned to different fields.
                </Alert>
                <br />
                <Alert severity="warning" style={{ textAlign: 'left' }}>
                  This action is permanent. Once upgraded, the older version
                  cannot be restored as it is no longer available in the Court
                  Forms library. Some of your customizations may need to be
                  re-applied.
                </Alert>
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialog} disabled={isUpgrading}>
            Cancel
          </Button>
          <Button onClick={handleDismissDialog} disabled={isUpgrading}>
            No, use current version
          </Button>
          <Button
            onClick={handleConfirmUpgrade}
            variant="contained"
            disabled={isUpgrading}
          >
            {isUpgrading ? 'Upgrading…' : 'Yes, upgrade to newer version'}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplatePreviewDialog
        open={isPreviewOpen}
        title={previewTitle}
        images={previewImages}
        isLoading={isPreviewLoading}
        onClose={closePreview}
      />
    </>
  );
};

export default TemplateDefaultValuesListWrapper;
